import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { chatApi } from "@/api/chat/chatApi.js";
import { getRealtimeHubConnection, startRealtimeHubConnection } from "@/utils/realtimeHub.js";
import {
  normalizeChatMessage,
  normalizeChatConversation,
  getConversationKey,
  deduplicateMessages,
} from "@/utils/chat/chatNormalizer.js";

/**
 * Dedicated single-responsibility hook for Staff Multi-Conversation Chat.
 */
export const useStaffChatSession = ({
  authContext = null,
  defaultDepartment = "Reception",
} = {}) => {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [otherUserTyping, setOtherUserTyping] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const [directory, setDirectory] = useState({
    activeGuests: [],
    staff: [],
    departments: [],
  });

  const activeConversationRef = useRef(activeConversation);
  const conversationsRef = useRef(conversations);
  const authContextRef = useRef(authContext);
  const loadingConversationIdRef = useRef(null);
  const typingTimerRef = useRef(null);

  useEffect(() => {
    activeConversationRef.current = activeConversation;
  }, [activeConversation]);

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  useEffect(() => {
    authContextRef.current = authContext;
  }, [authContext]);

  const currentUserId = authContext?.userId || authContext?.user?.id;
  const currentUserName = authContext?.userName || authContext?.user?.name || authContext?.user?.fullName || "Staff";

  // Load staff conversations, total unread count, and directory
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const [convsRes, unreadRes, dirRes] = await Promise.allSettled([
        chatApi.getConversations(),
        chatApi.getUnreadCount(),
        chatApi.getDirectory(),
      ]);

      if (convsRes.status === "fulfilled") {
        const rawList = Array.isArray(convsRes.value?.data?.data)
          ? convsRes.value.data.data
          : Array.isArray(convsRes.value?.data)
          ? convsRes.value.data
          : [];
        setConversations(rawList.map(normalizeChatConversation).filter(Boolean));
      }

      if (unreadRes.status === "fulfilled") {
        const u = typeof unreadRes.value?.data?.data === "number"
          ? unreadRes.value.data.data
          : typeof unreadRes.value?.data === "number"
          ? unreadRes.value.data
          : 0;
        setUnreadCount(u);
      }

      if (dirRes.status === "fulfilled") {
        const d = dirRes.value?.data?.data || dirRes.value?.data || {};
        setDirectory({
          activeGuests: Array.isArray(d.activeGuests) ? d.activeGuests : [],
          staff: Array.isArray(d.staff) ? d.staff : [],
          departments: Array.isArray(d.departments) ? d.departments : [],
        });
      }
    } catch (err) {
      console.error("[useStaffChatSession] Failed to load initial data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Select an active conversation
  const selectConversation = useCallback(async (conv) => {
    if (!conv) {
      setActiveConversation(null);
      setMessages([]);
      loadingConversationIdRef.current = null;
      return;
    }

    const normConv = normalizeChatConversation(conv) || conv;
    const convId = normConv.id;

    if (convId && activeConversationRef.current?.id === convId && loadingConversationIdRef.current === convId) {
      return;
    }

    loadingConversationIdRef.current = convId || "loading";
    setActiveConversation(normConv);

    try {
      setLoading(true);
      // Determine target user if StaffToStaff
      let otherStaffUserId = null;
      if (normConv.type === "StaffToStaff") {
        if (normConv.participant1UserId && currentUserId && String(normConv.participant1UserId).toLowerCase() !== String(currentUserId).toLowerCase()) {
          otherStaffUserId = normConv.participant1UserId;
        } else if (normConv.participant2UserId && currentUserId && String(normConv.participant2UserId).toLowerCase() !== String(currentUserId).toLowerCase()) {
          otherStaffUserId = normConv.participant2UserId;
        }
      }

      const res = await chatApi.getHistory({
        conversationId: convId || undefined,
        guestSessionId: normConv.guestSessionId || undefined,
        targetUserId: otherStaffUserId || undefined,
        targetDepartment: normConv.targetDepartment || undefined,
      });

      const rawList = Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data)
        ? res.data
        : [];
      setMessages(deduplicateMessages(rawList));

      // Mark as read
      if (convId) {
        await chatApi.markAsRead({ conversationId: convId }).catch(() => {});
        setConversations((prev) =>
          prev.map((c) => (c.id === convId ? { ...c, unreadCount: 0 } : c))
        );
        setUnreadCount((prev) => Math.max(0, prev - (normConv.unreadCount || 0)));
      }
    } catch (err) {
      console.error("[useStaffChatSession] Failed to load messages:", err);
    } finally {
      setLoading(false);
      loadingConversationIdRef.current = null;
    }
  }, [currentUserId]);

  // Start chat with an active guest room
  const startGuestChat = useCallback(
    async (guest) => {
      if (!guest?.sessionId) return;
      const key = `guest:${String(guest.sessionId).toLowerCase()}`;
      const existing = conversationsRef.current.find((c) => c.key === key || c.guestSessionId === guest.sessionId);
      if (existing) {
        selectConversation(existing);
        return;
      }

      const draftConv = normalizeChatConversation({
        id: guest.existingConversationId || null,
        type: "GuestToDepartment",
        guestSessionId: guest.sessionId,
        roomName: guest.roomName,
        title: `Room ${guest.roomName} (${guest.guestName})`,
        targetDepartment: "Reception",
      });

      selectConversation(draftConv);
    },
    [selectConversation]
  );

  // Start chat with a colleague
  const startStaffChat = useCallback(
    async (staffUser) => {
      if (!staffUser?.userId) return;
      const sorted = [String(currentUserId).toLowerCase(), String(staffUser.userId).toLowerCase()].sort();
      const key = `staff:${sorted[0]}_${sorted[1]}`;
      const existing = conversationsRef.current.find(
        (c) =>
          c.key === key ||
          (c.type === "StaffToStaff" &&
            ((c.participant1UserId === staffUser.userId && c.participant2UserId === currentUserId) ||
              (c.participant2UserId === staffUser.userId && c.participant1UserId === currentUserId)))
      );
      if (existing) {
        selectConversation(existing);
        return;
      }

      const draftConv = normalizeChatConversation({
        id: staffUser.existingConversationId || null,
        type: "StaffToStaff",
        participant1UserId: currentUserId,
        participant2UserId: staffUser.userId,
        otherParticipantName: staffUser.displayName || staffUser.email,
        title: staffUser.displayName || staffUser.email,
        targetDepartment: staffUser.department,
      });

      selectConversation(draftConv);
    },
    [selectConversation, currentUserId]
  );

  // Start chat with a department
  const startDepartmentChat = useCallback(
    async (deptKey, deptName) => {
      if (!deptKey) return;
      const key = `dept:${deptKey.toUpperCase()}`;
      const existing = conversationsRef.current.find(
        (c) => c.key === key || (c.targetDepartment && c.targetDepartment.toUpperCase() === deptKey.toUpperCase())
      );
      if (existing) {
        selectConversation(existing);
        return;
      }

      const draftConv = normalizeChatConversation({
        id: null,
        type: "Department",
        targetDepartment: deptKey,
        title: deptName || deptKey,
      });

      selectConversation(draftConv);
    },
    [selectConversation]
  );

  // Send a message
  const sendMessage = useCallback(
    async ({
      content,
      targetDepartment = defaultDepartment,
      recipientUserId = null,
      attachmentUrl = null,
      attachmentType = null,
    }) => {
      if (!content || !content.trim()) return;

      setSending(true);
      try {
        const active = activeConversationRef.current;
        const targetUser =
          recipientUserId ||
          (active?.participant1UserId === currentUserId ? active?.participant2UserId : active?.participant1UserId);

        const resolvedTargetType =
          active?.type === "StaffToStaff"
            ? "DirectStaff"
            : active?.guestSessionId || active?.type === "GuestToDepartment"
            ? "GuestSession"
            : "Department";

        const payload = {
          conversationId: active?.id || null,
          targetType: resolvedTargetType,
          targetDepartment: active?.targetDepartment || targetDepartment,
          recipientUserId: targetUser || null,
          guestSessionId: active?.guestSessionId || null,
          roomName: active?.roomName || null,
          senderDisplayName: currentUserName,
          content: content.trim(),
          attachmentUrl,
          attachmentType,
        };

        const res = await chatApi.sendMessage(payload);
        const rawMsg = res?.data?.data || res?.data;
        if (rawMsg) {
          const normMsg = normalizeChatMessage(rawMsg);
          setMessages((prev) => deduplicateMessages([...prev, normMsg]));

          if (normMsg.conversationId && (!active?.id || active.id !== normMsg.conversationId)) {
            setActiveConversation((prev) => (prev ? { ...prev, id: normMsg.conversationId } : prev));
          }

          // Update conversation list snippet
          setConversations((prev) => {
            const activeKey = active?.key || getConversationKey(normMsg);
            const idx = prev.findIndex((c) => (c.key && c.key === activeKey) || (c.id && c.id === normMsg.conversationId));
            if (idx !== -1) {
              const updated = [...prev];
              updated[idx] = {
                ...updated[idx],
                id: normMsg.conversationId || updated[idx].id,
                lastMessageSnippet: normMsg.content,
                lastMessageAt: normMsg.createdAt,
              };
              const [item] = updated.splice(idx, 1);
              return [item, ...updated];
            }
            return prev;
          });
        }
      } catch (err) {
        console.error("[useStaffChatSession] Failed to send message:", err);
        toast.error("Failed to send message.");
      } finally {
        setSending(false);
      }
    },
    [currentUserId, currentUserName, defaultDepartment]
  );

  // Send typing indicator
  const sendTyping = useCallback(
    async (typing) => {
      try {
        const active = activeConversationRef.current;
        const connection = getRealtimeHubConnection();
        if (connection && connection.state === "Connected" && active?.id) {
          await connection.invoke("SendChatTyping", {
            conversationId: active.id,
            targetDepartment: active.targetDepartment,
            guestSessionId: active.guestSessionId,
            isTyping: typing,
          });
        }
      } catch {}
    },
    []
  );

  // Realtime SignalR event listeners
  useEffect(() => {
    let connection;
    try {
      connection = getRealtimeHubConnection();
      startRealtimeHubConnection().catch(() => {});
    } catch {
      return;
    }

    const handleReceiveChatMessage = (incomingMsg) => {
      if (!incomingMsg) return;
      const normMsg = normalizeChatMessage(incomingMsg);
      if (!normMsg) return;

      const active = activeConversationRef.current;
      const activeKey = active?.key || getConversationKey(active);
      const incomingKey = normMsg.key || getConversationKey(normMsg);

      const isForActive =
        active &&
        ((active.id && normMsg.conversationId && active.id === normMsg.conversationId) ||
          (activeKey && incomingKey && activeKey === incomingKey));

      const isSenderMe =
        normMsg.senderUserId === currentUserId ||
        (normMsg.senderType === "Staff" &&
          normMsg.senderDisplayName &&
          normMsg.senderDisplayName.trim().toLowerCase() === currentUserName.trim().toLowerCase());

      if (isForActive) {
        setMessages((prev) => deduplicateMessages([...prev, normMsg]));

        if (normMsg.conversationId && (!active.id || active.id !== normMsg.conversationId)) {
          setActiveConversation((prev) => (prev ? { ...prev, id: normMsg.conversationId } : prev));
        }

        if (!isSenderMe && normMsg.conversationId) {
          chatApi.markAsRead({ conversationId: normMsg.conversationId }).catch(() => {});
        }
      } else if (!isSenderMe) {
        setUnreadCount((prev) => prev + 1);
        const title =
          normMsg.senderType === "Guest"
            ? (normMsg.roomName ? `Room ${normMsg.roomName}` : "Guest")
            : (normMsg.senderDisplayName || "Staff Colleague");
        toast.info(`💬 ${title}: ${normMsg.content.substring(0, 60)}`);
      }

      // Update conversations list snippet and unread badge
      setConversations((prev) => {
        const idx = prev.findIndex(
          (c) => (c.key && c.key === incomingKey) || (c.id && normMsg.conversationId && c.id === normMsg.conversationId)
        );
        if (idx !== -1) {
          const updated = [...prev];
          updated[idx] = {
            ...updated[idx],
            id: normMsg.conversationId || updated[idx].id,
            lastMessageSnippet: normMsg.content,
            lastMessageAt: normMsg.createdAt,
            unreadCount: isForActive || isSenderMe ? 0 : (updated[idx].unreadCount || 0) + 1,
          };
          const [item] = updated.splice(idx, 1);
          return [item, ...updated];
        } else {
          // New conversation incoming
          const newConv = normalizeChatConversation({
            id: normMsg.conversationId,
            type: normMsg.targetType === "DirectStaff" ? "StaffToStaff" : (normMsg.guestSessionId ? "GuestToDepartment" : "Department"),
            guestSessionId: normMsg.guestSessionId,
            roomName: normMsg.roomName,
            targetDepartment: normMsg.targetDepartment,
            participant1UserId: normMsg.senderUserId,
            participant2UserId: normMsg.recipientUserId,
            title: normMsg.senderDisplayName,
            lastMessageSnippet: normMsg.content,
            lastMessageAt: normMsg.createdAt,
            unreadCount: isSenderMe ? 0 : 1,
          });
          return [newConv, ...prev];
        }
      });
    };

    const handleChatTyping = (data) => {
      if (!data) return;
      const active = activeConversationRef.current;
      const activeKey = active?.key || getConversationKey(active);
      const typingKey = getConversationKey(data);

      if (activeKey && typingKey && activeKey !== typingKey) return;
      if (data.senderUserId === currentUserId) return;

      const isTyping = Boolean(data.isTyping ?? data.IsTyping);
      if (isTyping) {
        setOtherUserTyping({
          displayName: data.senderDisplayName || data.SenderDisplayName || "Colleague",
        });
        if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => setOtherUserTyping(null), 3000);
      } else {
        setOtherUserTyping(null);
      }
    };

    connection.on("ReceiveChatMessage", handleReceiveChatMessage);
    connection.on("receivechatmessage", handleReceiveChatMessage);
    connection.on("ChatTyping", handleChatTyping);
    connection.on("chattyping", handleChatTyping);

    return () => {
      connection.off("ReceiveChatMessage", handleReceiveChatMessage);
      connection.off("receivechatmessage", handleReceiveChatMessage);
      connection.off("ChatTyping", handleChatTyping);
      connection.off("chattyping", handleChatTyping);
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    };
  }, [currentUserId, currentUserName]);

  return {
    conversations,
    activeConversation,
    messages,
    directory,
    loading,
    sending,
    otherUserTyping,
    unreadCount,
    selectConversation,
    startGuestChat,
    startStaffChat,
    startDepartmentChat,
    sendMessage,
    sendTyping,
    loadInitialData,
  };
};
